import { useNavigate } from 'react-router-dom';
import { Plane, Package, ChevronDown, Check } from 'lucide-react';
import { useAppStore, type AppType } from '../../stores/app-store';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const APP_CONFIG: Record<
  AppType,
  { label: string; icon: React.ReactNode; color: string }
> = {
  TRAVEL: {
    label: 'Travel',
    icon: <Plane className="h-4 w-4" />,
    color: 'text-blue-500',
  },
  TRADING: {
    label: 'Trading',
    icon: <Package className="h-4 w-4" />,
    color: 'text-amber-500',
  },
};

export function AppSelector() {
  const navigate = useNavigate();
  const { currentApp, setCurrentApp, canAccessBothApps, getAccessibleApps } =
    useAppStore();

  // Don't render if user can only access one app
  if (!canAccessBothApps()) {
    return null;
  }

  const accessibleApps = getAccessibleApps();
  const currentConfig = currentApp ? APP_CONFIG[currentApp] : null;

  const handleAppSwitch = (app: AppType) => {
    if (app !== currentApp) {
      setCurrentApp(app);
      // Navigate to the dashboard of the selected app
      if (app === 'TRADING') {
        navigate('/trading');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 h-9 px-3 font-medium"
        >
          {currentConfig && (
            <>
              <span className={currentConfig.color}>{currentConfig.icon}</span>
              <span>{currentConfig.label}</span>
            </>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {accessibleApps.map((app) => {
          const config = APP_CONFIG[app];
          const isActive = app === currentApp;

          return (
            <DropdownMenuItem
              key={app}
              onClick={() => handleAppSwitch(app)}
              className="flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className={config.color}>{config.icon}</span>
                <span>{config.label}</span>
              </div>
              {isActive && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Compact version for mobile or sidebar
export function AppSelectorCompact() {
  const { currentApp, setCurrentApp, canAccessBothApps, getAccessibleApps } =
    useAppStore();
  const navigate = useNavigate();

  // Don't render if user can only access one app
  if (!canAccessBothApps()) {
    return null;
  }

  const accessibleApps = getAccessibleApps();

  const handleAppSwitch = (app: AppType) => {
    if (app !== currentApp) {
      setCurrentApp(app);
      if (app === 'TRADING') {
        navigate('/trading');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <div className="flex items-center border border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-background))]">
      {accessibleApps.map((app) => {
        const config = APP_CONFIG[app];
        const isActive = app === currentApp;

        return (
          <button
            key={app}
            onClick={() => handleAppSwitch(app)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                : 'text-[hsl(var(--sidebar-muted))] hover:text-white hover:bg-[hsl(var(--sidebar-accent))/50]'
            }`}
          >
            <span className={isActive ? 'text-white' : config.color}>{config.icon}</span>
            <span>{config.label}</span>
          </button>
        );
      })}
    </div>
  );
}
